/**
 * This file defines `tsWFDataModelHooks` which conforms to the the
 * `WFDataModelHooksInterface`, providing access to the Weaveflow data model
 * backed by the "Trace Server" engine.
 */

import {isSimpleTypeShape, union} from '@wandb/weave/core/model/helpers';
import * as _ from 'lodash';
import {isEmpty} from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useAsyncFn} from 'react-use';
import useAsync, {AsyncState} from 'react-use/lib/useAsync';
import useAsyncRetry from 'react-use/lib/useAsyncRetry';

import * as Types from '../../../../../../core/model/types';
import {useDeepMemo} from '../../../../../../hookUtils';
import {
  isWeaveObjectRef,
  parseRef,
  refUri,
  WeaveObjectRef,
} from '../../../../../../react';
import {
  callCache,
  objectVersionCache,
  opVersionCache,
  refDataCache,
} from './cache';
import {
  WANDB_ARTIFACT_REF_PREFIX,
  WEAVE_PRIVATE_PREFIX,
  WEAVE_REF_PREFIX,
} from './constants';
import * as traceServerClient from './traceServerClient';
import {useGetTraceServerClientContext} from './traceServerClientContext';
import * as traceServerTypes from './traceServerClientTypes';
import {
  ComputedCallStatuses,
  ComputedCallStatusType,
} from './traceServerClientTypes';
import {useClientSideCallRefExpansion} from './tsDataModelHooksCallRefExpansion';
import {opVersionRefOpName, refUriToObjectVersionKey} from './utilities';
import {
  CallSchema,
  Loadable,
  LoadableWithError,
  ObjectDeleteAllVersionsParams,
  ObjectDeleteParams,
  ObjectVersionKey,
  ObjectVersionSchema,
  OpVersionDeleteAllVersionsParams,
  OpVersionDeleteParams,
  OpVersionKey,
  OpVersionSchema,
  RawSpanFromStreamTableEra,
  Refetchable,
  UseApplyMutationsToRefParams,
  UseCallParams,
  UseCallsDeleteParams,
  UseCallsExportParams,
  UseCallsParams,
  UseCallsStatsParams,
  UseCallUpdateParams,
  UseFeedbackParams,
  UseFileContentParams,
  UseGetRefsTypeParams,
  UseObjCreateParams,
  UseObjectVersionParams,
  UseOpVersionParams,
  UseOpVersionsParams,
  UseProjectHasCallsParams,
  UseRefsDataParams,
  UseRefsReadBatchParams,
  UseRootObjectVersionsParams,
  UseTableQueryParams,
  UseTableQueryStatsParams,
  UseTableRowsQueryParams,
  UseTableUpdateParams,
  WeaveObjectVersionKey,
  WFDataModelHooksInterface,
} from './wfDataModelHooksInterface';

export const projectIdFromParts = ({
  entity,
  project,
}: {
  entity: string;
  project: string;
}) => `${entity}/${project}`;

// Trace server client keys that are promises
type TraceServerClientPromiseKeys = {
  [K in keyof traceServerClient.TraceServerClient]: traceServerClient.TraceServerClient[K] extends (
    ...args: any
  ) => Promise<any>
    ? K extends 'onCallDelete'
      ? never
      : K
    : never;
}[keyof traceServerClient.TraceServerClient];

export const makeTraceServerEndpointHook = <
  FN extends TraceServerClientPromiseKeys,
  Params extends object,
  Output,
>(
  traceServerFnName: FN,
  preprocessFn: (params: Params) => {
    params: Parameters<traceServerClient.TraceServerClient[FN]>[0];
    skip?: boolean;
  },
  postprocessFn: (
    res: Awaited<ReturnType<traceServerClient.TraceServerClient[FN]>>,
    params: Params
  ) => Output
) => {
  const useTraceServerRequest = (params: Params): LoadableWithError<Output> => {
    params = useDeepMemo(params);
    const loadingInputRef = useRef<Params | null>(null);
    const getTsClient = useGetTraceServerClientContext();
    const [state, setState] = useState<LoadableWithError<Output>>({
      loading: true,
      result: null,
      error: null,
    });

    useEffect(() => {
      loadingInputRef.current = params;
      setState({loading: true, result: null, error: null});
      const req = preprocessFn(params);
      if (req.skip) {
        setState({loading: false, result: null, error: null});
        return;
      }
      const client = getTsClient();
      client[traceServerFnName](req.params as any)
        .then(res => {
          if (params !== loadingInputRef.current) {
            return;
          }
          const output = postprocessFn(res as any, params);
          setState({loading: false, result: output, error: null});
        })
        .catch(err => {
          if (params !== loadingInputRef.current) {
            return;
          }
          setState({loading: false, result: null, error: err});
        });
    }, [getTsClient, params]);

    const loadingReturn = useMemo(
      () => ({loading: true, result: null, error: null}),
      []
    );
    if (loadingInputRef.current !== params) {
      return loadingReturn;
    }
    return state;
  };
  return useTraceServerRequest;
};

const useMakeTraceServerEndpoint = <
  FN extends TraceServerClientPromiseKeys,
  Input extends any[],
  Output,
>(
  traceServerFnName: FN,
  preprocessFn: (
    ...input: Input
  ) => Parameters<traceServerClient.TraceServerClient[FN]>[0],
  postprocessFn: (
    res: Awaited<ReturnType<traceServerClient.TraceServerClient[FN]>>
  ) => Output
) => {
  const getTsClient = useGetTraceServerClientContext();
  const client = getTsClient();
  const traceServerRequest = useCallback(
    (...input: Input): Promise<Output> => {
      return client[traceServerFnName](preprocessFn(...input) as any).then(
        res => {
          return postprocessFn(res as any);
        }
      );
    },
    [client, postprocessFn, preprocessFn, traceServerFnName]
  );
  return traceServerRequest;
};

// We only cache calls that have ended or errored. This is because we don't
// want to cache calls that are still running, as they may be updated in
// real time.
const shouldCacheCall = (call: traceServerTypes.TraceCallSchema) => {
  return call.ended_at != null || call.exception != null;
};

export const useCall = (
  params: UseCallParams
): LoadableWithError<CallSchema | null> & Refetchable => {
  const getTsClient = useGetTraceServerClientContext();
  const [error, setError] = useState<Error | null>(null);

  const effectiveKey = useMemo(() => {
    if (params.key == null) {
      return null;
    }
    return {
      ...params.key,
      withCosts: !!params.includeCosts,
      withTotalStorageSize: !!params.includeTotalStorageSize,
    };
  }, [params.key, params.includeCosts, params.includeTotalStorageSize]);
  const deepKey = useDeepMemo(effectiveKey);

  const cachedCall = deepKey ? callCache.get(deepKey) : null;
  const loadingRef = useRef(cachedCall == null);

  const [callRes, setCallRes] =
    useState<traceServerTypes.TraceCallReadRes | null>(null);
  const doFetch = useCallback(
    ({invalidateCache = false}: {invalidateCache?: boolean} = {}) => {
      if (deepKey) {
        if (invalidateCache) {
          callCache.del(deepKey);
        }
        loadingRef.current = true;
        setCallRes(null);
        setError(null);
        getTsClient()
          .callRead({
            project_id: projectIdFromParts(deepKey),
            id: deepKey.callId,
            include_costs: params.includeCosts,
            ...(params.includeTotalStorageSize
              ? {include_total_storage_size: true}
              : null),
          })
          .then(res => {
            loadingRef.current = false;
            setCallRes(res);
          })
          .catch(err => {
            loadingRef.current = false;
            setError(err);
          });
      }
    },
    [deepKey, getTsClient, params.includeCosts, params.includeTotalStorageSize]
  );

  useEffect(() => {
    doFetch({invalidateCache: false});
  }, [doFetch]);

  useEffect(() => {
    if (params.refetchOnRename) {
      const client = getTsClient();
      const unregisterRename = client.registerOnRenameListener(() =>
        doFetch({invalidateCache: true})
      );
      return () => {
        unregisterRename();
      };
    }
    return undefined;
  }, [getTsClient, doFetch, deepKey, params.refetchOnRename]);

  const refetch = useCallback(() => {
    doFetch({invalidateCache: true});
  }, [doFetch]);

  return useMemo(() => {
    if (deepKey == null) {
      return {
        loading: false,
        result: null,
        error: null,
        refetch,
      };
    } else if (cachedCall != null) {
      return {
        loading: false,
        result: cachedCall,
        error: error,
        refetch,
      };
    }
    const result =
      callRes && 'call' in callRes && callRes.call
        ? traceCallToUICallSchema(callRes.call)
        : null;

    if (
      result &&
      !error &&
      result.callId === deepKey.callId &&
      result.traceCall &&
      shouldCacheCall(result.traceCall)
    ) {
      callCache.set(deepKey, result);
    }

    return {
      loading: loadingRef.current,
      result: result,
      error: error,
      refetch,
    };
  }, [cachedCall, callRes, deepKey, error, refetch]);
};

const useCallsNoExpansion = (
  params: UseCallsParams
): Loadable<CallSchema[]> & Refetchable => {
  const getTsClient = useGetTraceServerClientContext();
  const loadingRef = useRef(false);
  const [callRes, setCallRes] =
    useState<traceServerTypes.TraceCallsQueryRes | null>(null);
  const [error, setError] = useState<Error | null>(null);
  params = useDeepMemo(params);

  const req = useMemo((): traceServerTypes.TraceCallsQueryReq => {
    return {
      project_id: projectIdFromParts({
        entity: params.entity,
        project: params.project,
      }),
      filter: {
        op_names: params.filter.opVersionRefs,
        input_refs: params.filter.inputObjectVersionRefs,
        output_refs: params.filter.outputObjectVersionRefs,
        parent_ids: params.filter.parentIds,
        trace_ids: params.filter.traceId
          ? [params.filter.traceId]
          : params.filter.traceIds,
        call_ids: params.filter.callIds,
        trace_roots_only: params.filter.traceRootsOnly,
        wb_run_ids: params.filter.runIds,
        wb_user_ids: params.filter.userIds,
      },
      limit: params.limit,
      offset: params.offset,
      sort_by: params.sortBy,
      query: params.query,
      columns: params.columns,
      expand_columns: params.expandColumns
        ? Array.from(params.expandColumns)
        : undefined,
      // Never expand server side, we handle this manually in the frontend
      return_expanded_column_values: false,
      include_costs: params.includeCosts,
      include_feedback: params.includeFeedback,
      ...(params.includeStorageSize ? {include_storage_size: true} : null),
      ...(params.includeTotalStorageSize
        ? {include_total_storage_size: true}
        : null),
    };
  }, [
    params.entity,
    params.project,
    params.filter,
    params.limit,
    params.offset,
    params.sortBy,
    params.query,
    params.columns,
    params.expandColumns,
    params.includeCosts,
    params.includeFeedback,
    params.includeStorageSize,
    params.includeTotalStorageSize,
  ]);

  // Keep track of the request we're waiting for, so that we
  // can ignore requests that are superseded by more recent reqs
  const expectedRequestRef = useRef(req);

  const abortControllerRef = useRef<AbortController | null>(null);
  const skipRef = useRef(params.skip);
  skipRef.current = params.skip;

  const doFetch = useCallback(() => {
    // Respect skip even when called from delete/rename listeners
    if (skipRef.current) {
      return;
    }

    // Abort any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    const currentAbortController = abortControllerRef.current;

    setCallRes(null);
    loadingRef.current = true;
    expectedRequestRef.current = req;

    const onSuccess = (res: traceServerTypes.TraceCallsQueryRes) => {
      // Only update state if this response matches our current request
      if (_.isEqual(expectedRequestRef.current, req)) {
        loadingRef.current = false;
        setCallRes(res);
        setError(null);
      }
    };
    const onError = (e: any) => {
      // Ignore abort errors
      if (e.name === 'AbortError') {
        return;
      }
      // Only update state if this response matches our current request
      if (_.isEqual(expectedRequestRef.current, req)) {
        loadingRef.current = false;
        console.error(e);
        setError(e);
        setCallRes({calls: []});
      }
    };
    getTsClient()
      .callsStreamQuery(req, currentAbortController.signal)
      .then(onSuccess)
      .catch(onError);
  }, [getTsClient, req]);

  // register doFetch as a callback after deletion
  useEffect(() => {
    if (params.refetchOnDelete) {
      const client = getTsClient();
      const unregisterDelete = client.registerOnDeleteListener(doFetch);
      const unregisterRename = client.registerOnRenameListener(doFetch);
      return () => {
        unregisterDelete();
        unregisterRename();
      };
    }
    return () => {};
  }, [params.refetchOnDelete, getTsClient, doFetch]);

  useEffect(() => {
    if (params.skip) {
      return;
    }
    doFetch();

    return () => {
      // Abort pending request on unmount or when dependencies change
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [params.skip, doFetch]);

  const refetch = useCallback(() => {
    doFetch();
  }, [doFetch]);

  return useMemo(() => {
    if (params.skip) {
      return {
        loading: false,
        result: [],
        refetch,
      };
    }
    const allResults = (callRes?.calls ?? [])
      .filter(isValidTraceCall)
      .map(traceCallToUICallSchema);
    const result = allResults;

    if (callRes == null || loadingRef.current) {
      return {
        loading: true,
        result: [],
        refetch,
      };
    } else {
      // Check if the query contained a column request. Only cache calls
      // if no columns were requested, only then are we guaranteed to get
      // all the call data
      if (!params.columns) {
        allResults.forEach(call => {
          if (call.traceCall && shouldCacheCall(call.traceCall)) {
            callCache.set(
              {
                entity: params.entity,
                project: params.project,
                callId: call.callId,
              },
              call
            );
          }
        });
      }
      return {
        loading: false,
        result,
        refetch,
        error,
      };
    }
  }, [params, callRes, refetch, error]);
};

export const useCalls = (
  params: UseCallsParams
): Loadable<CallSchema[]> & Refetchable => {
  const calls = useCallsNoExpansion(params);

  // This is a temporary solution until the trace server supports
  // backend expansions of refs. We should expect to see this go away, and
  // this entire function replaced with the contents of `useCallsNoExpansion`.
  const {expandedCalls, isExpanding} = useClientSideCallRefExpansion(
    calls,
    params.expandColumns
  );

  const loading = calls.loading || isExpanding;
  return useMemo(() => {
    return {
      loading,
      result: loading ? [] : expandedCalls.map(traceCallToUICallSchema),
      refetch: calls.refetch,
      error: calls.error,
    };
  }, [calls.refetch, expandedCalls, loading, calls.error]);
};

export const useCallsStats = (
  params: UseCallsStatsParams
): Loadable<traceServerTypes.TraceCallsQueryStatsRes> & Refetchable => {
  const getTsClient = useGetTraceServerClientContext();
  const loadingRef = useRef(false);
  const [callStatsRes, setCallStatsRes] =
    useState<LoadableWithError<traceServerTypes.TraceCallsQueryStatsRes> | null>(
      null
    );
  const deepFilter = useDeepMemo(params.filter);

  const doFetch = useCallback(() => {
    if (params.skip) {
      setCallStatsRes({loading: false, result: null, error: null});
      return;
    }
    loadingRef.current = true;
    setCallStatsRes(null);

    const req: traceServerTypes.TraceCallsQueryStatsReq = {
      project_id: projectIdFromParts({
        entity: params.entity,
        project: params.project,
      }),
      filter: deepFilter
        ? {
            op_names: deepFilter?.opVersionRefs,
            input_refs: deepFilter?.inputObjectVersionRefs,
            output_refs: deepFilter?.outputObjectVersionRefs,
            parent_ids: deepFilter?.parentIds,
            trace_ids: deepFilter?.traceId
              ? [deepFilter.traceId]
              : deepFilter?.traceIds,
            call_ids: deepFilter?.callIds,
            trace_roots_only: deepFilter?.traceRootsOnly,
            wb_run_ids: deepFilter?.runIds,
            wb_user_ids: deepFilter?.userIds,
          }
        : undefined,
      query: params.query,
      limit: params.limit,
      expand_columns: params.expandColumns
        ? Array.from(params.expandColumns)
        : undefined,
      ...(params.includeTotalStorageSize
        ? {include_total_storage_size: true}
        : null),
    };

    getTsClient()
      .callsQueryStats(req)
      .then(res => {
        loadingRef.current = false;
        setCallStatsRes({loading: false, result: res, error: null});
      })
      .catch(err => {
        loadingRef.current = false;
        setCallStatsRes({loading: false, result: null, error: err});
      });
  }, [
    params.skip,
    params.includeTotalStorageSize,
    params.entity,
    params.project,
    deepFilter,
    params.query,
    params.limit,
    params.expandColumns,
    getTsClient,
  ]);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  useEffect(() => {
    if (!params.refetchOnDelete) {
      return;
    }
    return getTsClient().registerOnDeleteListener(doFetch);
  }, [getTsClient, doFetch, params.refetchOnDelete]);

  const refetch = useCallback(() => {
    doFetch();
  }, [doFetch]);

  return useMemo(() => {
    if (params.skip) {
      return {loading: false, result: null, error: null, refetch};
    } else {
      if (callStatsRes == null || loadingRef.current) {
        return {loading: true, result: null, error: null, refetch};
      }
      return {...callStatsRes, refetch};
    }
  }, [callStatsRes, params.skip, refetch]);
};

const useProjectHasCalls = (
  params: UseProjectHasCallsParams
): Loadable<boolean> => {
  const callsStats = useCallsStats({
    entity: params.entity,
    project: params.project,
    limit: 1,
    skip: params.skip,
  });
  const count = callsStats.result?.count ?? 0;
  return useMemo(() => {
    return {
      loading: callsStats.loading,
      result: count > 0,
      error: callsStats.error,
    };
  }, [callsStats, count]);
};

const useCallsDeleteFunc = () => {
  const getTsClient = useGetTraceServerClientContext();

  const callsDelete = useCallback(
    (params: UseCallsDeleteParams): Promise<void> => {
      return getTsClient()
        .callsDelete({
          project_id: projectIdFromParts({
            entity: params.entity,
            project: params.project,
          }),
          call_ids: params.callIDs,
        })
        .then(() => {
          params.callIDs.forEach(callId => {
            callCache.del({
              entity: params.entity,
              project: params.project,
              callId,
            });
          });
        });
    },
    [getTsClient]
  );

  return callsDelete;
};

const useCallUpdateFunc = () => {
  const getTsClient = useGetTraceServerClientContext();

  const callUpdate = useCallback(
    (params: UseCallUpdateParams): Promise<void> => {
      return getTsClient()
        .callUpdate({
          project_id: projectIdFromParts({
            entity: params.entity,
            project: params.project,
          }),
          call_id: params.callID,
          display_name: params.newName,
        })
        .then(() => {
          callCache.del({
            entity: params.entity,
            project: params.project,
            callId: params.callID,
          });
        });
    },
    [getTsClient]
  );

  return callUpdate;
};

const useCallsExport = () => {
  const getTsClient = useGetTraceServerClientContext();

  const downloadCallsExport = useCallback(
    (params: UseCallsExportParams) => {
      const req: traceServerTypes.TraceCallsQueryReq = {
        project_id: projectIdFromParts({
          entity: params.entity,
          project: params.project,
        }),
        filter: {
          op_names: params.filter.opVersionRefs,
          input_refs: params.filter.inputObjectVersionRefs,
          output_refs: params.filter.outputObjectVersionRefs,
          parent_ids: params.filter.parentIds,
          trace_ids: params.filter.traceId
            ? [params.filter.traceId]
            : undefined,
          call_ids: params.filter.callIds,
          trace_roots_only: params.filter.traceRootsOnly,
          wb_run_ids: params.filter.runIds,
          wb_user_ids: params.filter.userIds,
        },
        limit: params.limit,
        offset: params.offset,
        sort_by: params.sortBy,
        query: params.query,
        columns: params.columns ?? undefined,
        expand_columns: params.expandedRefCols ?? undefined,
        include_feedback: params.includeFeedback ?? false,
        include_costs: params.includeCosts ?? false,
        include_total_storage_size: (params.columns ?? []).includes(
          'total_storage_size_bytes'
        ),
      };
      return getTsClient().callsStreamDownload(req, params.contentType);
    },
    [getTsClient]
  );

  return downloadCallsExport;
};

const useFeedback = (
  params: UseFeedbackParams
): LoadableWithError<traceServerTypes.Feedback[]> & Refetchable => {
  const getTsClient = useGetTraceServerClientContext();

  const [result, setResult] = useState<
    LoadableWithError<traceServerTypes.Feedback[]>
  >({
    loading: false,
    result: null,
    error: null,
  });
  const [doReload, setDoReload] = useState(false);
  const refetch = useCallback(() => {
    setDoReload(true);
  }, [setDoReload]);

  const deepKey = useDeepMemo(params.key);

  useEffect(() => {
    let mounted = true;
    if (doReload) {
      setDoReload(false);
    }
    if (!deepKey) {
      return;
    }
    setResult({loading: true, result: null, error: null});
    getTsClient()
      .feedbackQuery({
        project_id: projectIdFromParts({
          entity: deepKey.entity,
          project: deepKey.project,
        }),
        query: {
          $expr: {
            $eq: [{$getField: 'weave_ref'}, {$literal: deepKey.weaveRef}],
          },
        },
        sort_by: params.sortBy ?? [{field: 'created_at', direction: 'desc'}],
      })
      .then(res => {
        if (!mounted) {
          return;
        }
        if ('result' in res) {
          setResult({loading: false, result: res.result, error: null});
        }
        // TODO: handle error case
      })
      .catch(err => {
        if (!mounted) {
          return;
        }
        setResult({loading: false, result: null, error: err});
      });
    return () => {
      mounted = false;
    };
  }, [deepKey, getTsClient, doReload, params.sortBy]);

  return {...result, refetch};
};

const useOpVersion = (
  params: UseOpVersionParams
): LoadableWithError<OpVersionSchema | null> => {
  const getTsClient = useGetTraceServerClientContext();
  const loadingRef = useRef(false);
  const cachedOpVersion = params.key ? opVersionCache.get(params.key) : null;
  const [opVersionRes, setOpVersionRes] =
    useState<traceServerTypes.TraceObjReadRes | null>(null);
  const [error, setError] = useState<any>(null);
  const deepKey = useDeepMemo(params.key);
  useEffect(() => {
    if (deepKey) {
      setOpVersionRes(null);
      loadingRef.current = true;
      getTsClient()
        .objRead({
          project_id: projectIdFromParts({
            entity: deepKey?.entity ?? '',
            project: deepKey?.project ?? '',
          }),
          object_id: deepKey?.opId ?? '',
          digest: deepKey?.versionHash ?? '',
          metadata_only: params.metadataOnly ?? false,
        })
        .then(res => {
          loadingRef.current = false;
          setOpVersionRes(res);
          if (res.obj == null && !params.metadataOnly) {
            setError(new Error(JSON.stringify(res)));
            // be conservative and unset the cache when there's an error
            if (deepKey) {
              opVersionCache.del(deepKey);
            }
          }
        });
    }
  }, [deepKey, getTsClient, params.metadataOnly]);

  return useMemo(() => {
    if (params.key == null) {
      return {
        loading: false,
        result: null,
        error,
      };
    }
    if (cachedOpVersion != null) {
      return {
        loading: false,
        result: cachedOpVersion,
        error,
      };
    }
    if (opVersionRes == null || loadingRef.current) {
      return {
        loading: true,
        result: null,
        error,
      };
    }

    if (opVersionRes.obj == null) {
      return {
        loading: false,
        result: null,
        error,
      };
    }

    const returnedResult = convertTraceServerObjectVersionToOpSchema(
      opVersionRes.obj
    );

    if (
      params.key.entity !== returnedResult.entity ||
      params.key.project !== returnedResult.project ||
      params.key.opId !== returnedResult.opId ||
      params.key.versionHash !== returnedResult.versionHash
    ) {
      return {
        loading: true,
        result: null,
        error,
      };
    }

    const cacheableResult: OpVersionSchema = {
      ...params.key,
      ...returnedResult,
    };

    // Skip setting the cache if metadata only
    if (params.metadataOnly) {
      return {
        loading: false,
        result: cacheableResult,
        error,
      };
    }

    opVersionCache.set(params.key, cacheableResult);
    return {
      loading: false,
      result: cacheableResult,
      error,
    };
  }, [cachedOpVersion, params.key, opVersionRes, error, params.metadataOnly]);
};

const useOpVersions = (
  params: UseOpVersionsParams
): LoadableWithError<OpVersionSchema[]> => {
  const getTsClient = useGetTraceServerClientContext();
  const loadingRef = useRef(false);
  const [opVersionRes, setOpVersionRes] = useState<
    LoadableWithError<OpVersionSchema[]>
  >({
    loading: false,
    error: null,
    result: null,
  });
  const deepFilter = useDeepMemo(params.filter);
  const deepOrderBy = useDeepMemo(params.orderBy);

  const doFetch = useCallback(() => {
    if (params.skip) {
      return;
    }
    setOpVersionRes({loading: true, error: null, result: null});
    loadingRef.current = true;

    const req: traceServerTypes.TraceObjQueryReq = {
      project_id: projectIdFromParts({
        entity: params.entity,
        project: params.project,
      }),
      filter: {
        object_ids: deepFilter.opIds,
        latest_only: deepFilter.latestOnly,
        is_op: true,
      },
      limit: params.limit,
      metadata_only: params.metadataOnly,
      sort_by: deepOrderBy,
    };
    const onSuccess = (res: traceServerTypes.TraceObjQueryRes) => {
      loadingRef.current = false;
      setOpVersionRes({
        loading: false,
        error: null,
        result: res.objs.map(convertTraceServerObjectVersionToOpSchema),
      });
    };
    const onError = (e: any) => {
      loadingRef.current = false;
      console.error(e);
      setOpVersionRes({loading: false, error: e, result: null});
    };
    getTsClient().objsQuery(req).then(onSuccess).catch(onError);
  }, [
    deepFilter,
    getTsClient,
    params.skip,
    params.entity,
    params.project,
    params.limit,
    params.metadataOnly,
    deepOrderBy,
  ]);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  useEffect(() => {
    return getTsClient().registerOnObjectListener(doFetch);
  }, [getTsClient, doFetch]);

  return useMemo(() => {
    if (params.skip) {
      return {loading: false, error: null, result: null};
    }
    if (opVersionRes == null || loadingRef.current) {
      return {loading: true, error: null, result: null};
    }
    return opVersionRes;
  }, [opVersionRes, params.skip]);
};

// Helper function to convert trace server object version to op schema
const convertTraceServerObjectVersionToOpSchema = (
  obj: traceServerTypes.TraceObjSchema
): OpVersionSchema => {
  const {entity, project} = projectIdToParts(obj.project_id);
  return {
    entity,
    project,
    opId: obj.object_id,
    versionHash: obj.digest,
    createdAtMs: convertISOToDate(obj.created_at).getTime(),
    versionIndex: obj.version_index,
    userId: obj.wb_user_id ?? undefined,
  };
};

const useFileContent = (
  params: UseFileContentParams
): LoadableWithError<ArrayBuffer> => {
  const getTsClient = useGetTraceServerClientContext();
  const loadingRef = useRef(false);
  const [fileContentRes, setFileContentRes] =
    useState<traceServerTypes.TraceFileContentReadRes | null>(null);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    if (params.skip) {
      return;
    }
    setFileContentRes(null);
    loadingRef.current = true;
    getTsClient()
      .fileContent({
        project_id: projectIdFromParts({
          entity: params.entity,
          project: params.project,
        }),
        digest: params.digest,
      })
      .then(res => {
        loadingRef.current = false;
        setFileContentRes(res);
      })
      .catch(err => {
        loadingRef.current = false;
        setError(err);
      });
  }, [getTsClient, params.entity, params.project, params.digest, params.skip]);

  return useMemo(() => {
    if (params.skip) {
      return {loading: false, result: null, error: null};
    }
    if (fileContentRes == null || loadingRef.current) {
      return {loading: true, result: null, error};
    }
    return {loading: false, result: fileContentRes.content, error};
  }, [fileContentRes, params.skip, error]);
};

export const useObjectVersion = (
  params: UseObjectVersionParams
): LoadableWithError<ObjectVersionSchema | null> => {
  const getTsClient = useGetTraceServerClientContext();
  const loadingRef = useRef(false);
  const cachedObjectVersion = params.key
    ? objectVersionCache.get(params.key)
    : null;
  const [objectVersionRes, setObjectVersionRes] =
    useState<traceServerTypes.TraceObjReadRes | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const deepKey = useDeepMemo(params.key);
  useEffect(() => {
    if (deepKey) {
      setObjectVersionRes(null);
      loadingRef.current = true;
      getTsClient()
        .objRead({
          project_id: projectIdFromParts({
            entity: deepKey?.entity ?? '',
            project: deepKey?.project ?? '',
          }),
          object_id: deepKey?.objectId ?? '',
          digest: deepKey?.versionHash ?? '',
          metadata_only: params.metadataOnly ?? false,
          ...(params.includeTagsAndAliases
            ? {include_tags_and_aliases: true}
            : null),
        })
        .then(res => {
          loadingRef.current = false;
          if (res.obj == null) {
            if ('deleted_at' in res) {
              setError(new Error(JSON.stringify(res)));
            } else {
              setError(new Error('Object not found'));
            }
          } else {
            setObjectVersionRes(res);
          }
        })
        .catch(err => {
          setError(new Error(JSON.stringify(err)));
        });
    }
  }, [deepKey, getTsClient, params.metadataOnly, params.includeTagsAndAliases]);

  return useMemo(() => {
    if (params.key == null) {
      return {
        loading: false,
        result: null,
        error,
      };
    }
    if (cachedObjectVersion != null) {
      return {
        loading: false,
        result: cachedObjectVersion,
        error,
      };
    }
    if (objectVersionRes == null || loadingRef.current) {
      return {
        loading: true,
        result: null,
        error,
      };
    }

    if (objectVersionRes.obj == null) {
      return {
        loading: false,
        result: null,
        error,
      };
    }

    const returnedResult: ObjectVersionSchema =
      convertTraceServerObjectVersionToSchema(objectVersionRes.obj);

    if (
      params.key.entity !== returnedResult.entity ||
      params.key.project !== returnedResult.project ||
      params.key.objectId !== returnedResult.objectId ||
      params.key.versionHash !== returnedResult.versionHash
    ) {
      return {
        loading: true,
        result: null,
        error,
      };
    }

    const cacheableResult: ObjectVersionSchema = {
      ...params.key,
      ...returnedResult,
    };

    // Skip setting the cache if metadata only
    if (params.metadataOnly) {
      return {
        loading: false,
        result: cacheableResult,
        error,
      };
    }

    objectVersionCache.set(params.key, cacheableResult);
    return {
      loading: false,
      result: cacheableResult,
      error,
    };
  }, [
    cachedObjectVersion,
    params.key,
    objectVersionRes,
    error,
    params.metadataOnly,
  ]);
};

type UseRegistryPromptObjectParams = {
  entityName?: string;
  projectName?: string;
  collectionName?: string;
  version?: string;
};

export const useRegistryPromptObject = makeTraceServerEndpointHook<
  'objRead',
  UseRegistryPromptObjectParams,
  traceServerTypes.TraceObjReadRes['obj']
>(
  'objRead',
  params => ({
    params: {
      project_id: projectIdFromParts({
        entity: params.entityName ?? '',
        project: params.projectName ?? '',
      }),
      object_id: params.collectionName ?? '',
      digest: params.version ?? '',
    },
    skip:
      !params.entityName ||
      !params.projectName ||
      !params.collectionName ||
      !params.version,
  }),
  res => res.obj
);

export const convertTraceServerObjectVersionToSchema = <
  T extends traceServerTypes.TraceObjSchema,
>(
  obj: T
): ObjectVersionSchema<T['val']> => {
  const [entity, project] = obj.project_id.split('/');
  return {
    scheme: 'weave' as const,
    entity,
    project,
    weaveKind: 'object' as const,
    objectId: obj.object_id,
    versionHash: obj.digest,
    path: 'obj',
    createdAtMs: convertISOToDate(obj.created_at).getTime(),
    baseObjectClass: obj.base_object_class ?? null,
    leafObjectClass: obj.leaf_object_class ?? null,
    versionIndex: obj.version_index,
    val: obj.val,
    userId: obj.wb_user_id ?? undefined,
    sizeBytes: obj.size_bytes ?? undefined,
    tags: obj.tags ?? undefined,
    aliases: obj.aliases ?? undefined,
  };
};

export const useRootObjectVersions = (
  params: UseRootObjectVersionsParams
): LoadableWithError<ObjectVersionSchema[]> & {refetch: () => void} => {
  const getTsClient = useGetTraceServerClientContext();
  const deepFilter = useDeepMemo(params.filter);

  const [state, doFetch] = useAsyncFn(async () => {
    const req: traceServerTypes.TraceObjQueryReq = {
      project_id: projectIdFromParts({
        entity: params.entity,
        project: params.project,
      }),
      filter: {
        base_object_classes: deepFilter?.baseObjectClasses,
        leaf_object_classes: deepFilter?.leafObjectClasses,
        exclude_base_object_classes: deepFilter?.excludeBaseObjectClasses,
        object_ids: deepFilter?.objectIds,
        latest_only: deepFilter?.latestOnly,
        is_op: false,
        tags: deepFilter?.tags,
        aliases: deepFilter?.aliases,
      },
      limit: params.limit,
      metadata_only: params.metadataOnly,
      sort_by: params.sortBy,
      ...(params.includeStorageSize ? {include_storage_size: true} : null),
      ...(params.includeTagsAndAliases
        ? {include_tags_and_aliases: true}
        : null),
    };
    const res = await getTsClient().objsQuery(req);
    return res.objs?.map(convertTraceServerObjectVersionToSchema) ?? [];
  }, [
    params.entity,
    params.project,
    deepFilter,
    params.limit,
    params.metadataOnly,
    params.includeStorageSize,
    params.includeTagsAndAliases,
    params.sortBy,
    getTsClient,
  ]);

  useEffect(() => {
    if (!params.skip) {
      doFetch();
    }
  }, [doFetch, params.skip]);

  useEffect(() => {
    if (params.skip || params.noAutoRefresh) {
      return;
    }
    return getTsClient().registerOnObjectListener(doFetch);
  }, [getTsClient, doFetch, params.skip, params.noAutoRefresh]);

  return useMemo(() => {
    if (params.skip) {
      return {loading: false, error: null, result: null, refetch: doFetch};
    }
    return {
      loading: state.loading,
      error: state.error ?? null,
      result: state.value ?? null,
      refetch: doFetch,
    };
  }, [state, params.skip, doFetch]);
};

const useObjectDeleteFunc = () => {
  const getTsClient = useGetTraceServerClientContext();

  const makeObjectRef = (key: ObjectVersionKey) => {
    const ref: WeaveObjectRef = {
      scheme: 'weave',
      entityName: key.entity,
      projectName: key.project,
      weaveKind: 'object',
      artifactName: key.objectId,
      artifactVersion: key.versionHash,
    };
    return refUri(ref);
  };

  const makeOpRef = (key: OpVersionKey) => {
    const ref: WeaveObjectRef = {
      scheme: 'weave',
      entityName: key.entity,
      projectName: key.project,
      weaveKind: 'op',
      artifactName: key.opId,
      artifactVersion: key.versionHash,
    };
    return refUri(ref);
  };

  const updateObjectCaches = useCallback((key: ObjectVersionKey) => {
    objectVersionCache.del(key);
    const ref = makeObjectRef(key);
    refDataCache.del(ref);
  }, []);

  const updateOpCaches = useCallback((key: OpVersionKey) => {
    opVersionCache.del(key);
    const ref = makeOpRef(key);
    refDataCache.del(ref);
  }, []);

  const objectVersionsDelete = useCallback(
    (params: ObjectDeleteParams) => {
      params.digests?.forEach(digest => {
        updateObjectCaches({
          scheme: 'weave',
          weaveKind: 'object',
          entity: params.entity,
          project: params.project,
          objectId: params.objectId,
          versionHash: digest,
          path: '',
        });
      });
      return getTsClient().objDelete({
        project_id: projectIdFromParts({
          entity: params.entity,
          project: params.project,
        }),
        object_id: params.objectId,
        digests: params.digests,
      });
    },
    [getTsClient, updateObjectCaches]
  );

  const objectDeleteAllVersions = useCallback(
    (params: ObjectDeleteAllVersionsParams) => {
      updateObjectCaches(params.key);
      return getTsClient().objDelete({
        project_id: projectIdFromParts({
          entity: params.key.entity,
          project: params.key.project,
        }),
        object_id: params.key.objectId,
        digests: [],
      });
    },
    [getTsClient, updateObjectCaches]
  );

  const opVersionsDelete = useCallback(
    (params: OpVersionDeleteParams) => {
      params.digests?.forEach(digest => {
        updateOpCaches({
          entity: params.entity,
          project: params.project,
          opId: params.opId,
          versionHash: digest,
        });
      });
      return getTsClient().objDelete({
        project_id: projectIdFromParts({
          entity: params.entity,
          project: params.project,
        }),
        object_id: params.opId,
        digests: params.digests,
      });
    },
    [getTsClient, updateOpCaches]
  );

  const opDeleteAllVersions = useCallback(
    (params: OpVersionDeleteAllVersionsParams) => {
      updateOpCaches(params.key);
      return getTsClient().objDelete({
        project_id: projectIdFromParts({
          entity: params.key.entity,
          project: params.key.project,
        }),
        object_id: params.key.opId,
        digests: [],
      });
    },
    [getTsClient, updateOpCaches]
  );

  return {
    objectVersionsDelete,
    objectDeleteAllVersions,
    opVersionsDelete,
    opDeleteAllVersions,
  };
};

export {useObjectDeleteFunc};

export const useRefsReadBatch = (
  params: UseRefsReadBatchParams
): Loadable<any[]> => {
  const getTsClient = useGetTraceServerClientContext();
  // Here we keep track of the result and the uris we fetched for. This is
  // because repeated calls to this hook with different uris will return
  // could result in stale results if we just returned the result from the
  // hook.
  const [refsRes, setRefsRes] = useState<{
    res: traceServerTypes.TraceRefsReadBatchRes;
    forUris: string[];
  } | null>(null);
  const loadingRef = useRef(false);

  const deepRefUris = useDeepMemo(params.refUris);

  useEffect(() => {
    if (params.skip || deepRefUris.length === 0) {
      return;
    }
    setRefsRes(null);
    loadingRef.current = true;
    getTsClient()
      .readBatch({
        refs: deepRefUris,
      })
      .then((res: traceServerTypes.TraceRefsReadBatchRes) => {
        loadingRef.current = false;
        setRefsRes({res, forUris: deepRefUris});
      })
      .catch((err: any) => {
        loadingRef.current = false;
        // Check if this is a 404 error (ref missing)
        if (err.statusCode === 404) {
          // Treat all URIs as missing (set to null)
          const emptyResults = deepRefUris.map(() => null);
          setRefsRes({
            res: {vals: emptyResults},
            forUris: deepRefUris,
          });
        } else {
          console.error('Error fetching refs:', err);
          setRefsRes(null);
        }
      });
  }, [getTsClient, deepRefUris, params.skip]);

  return useMemo(() => {
    if (params.skip || params.refUris.length === 0) {
      return {loading: false, result: null};
    }
    if (
      refsRes == null ||
      loadingRef.current ||
      refsRes.forUris !== deepRefUris
    ) {
      return {loading: true, result: null};
    }
    return {loading: false, result: refsRes.res.vals};
  }, [params.skip, params.refUris.length, refsRes, deepRefUris]);
};

const useTableQuery = makeTraceServerEndpointHook<
  'tableQuery',
  UseTableQueryParams,
  traceServerTypes.TraceTableQueryRes['rows']
>(
  'tableQuery',
  params => ({
    params: {
      project_id: params.projectId,
      digest: params.digest,
      filter: params.filter,
      limit: params.limit,
      offset: params.offset,
      sort_by: params.sortBy,
    },
    skip: params.skip,
  }),
  res => res.rows
);

const useRefsData = (params: UseRefsDataParams): Loadable<any[]> => {
  const refUrisDeep = useDeepMemo(params.refUris);

  const [nonTableRefUris, tableRefUris] = useMemo(() => {
    const sUris: string[] = [];
    const tUris: string[] = [];
    refUrisDeep
      .map(uri => {
        return {uri, ref: refUriToObjectVersionKey(uri)};
      })
      .forEach(({uri, ref}, ndx) => {
        if (ref) {
          if (ref.scheme === 'weave' && ref.weaveKind === 'table') {
            tUris.push(uri);
          } else {
            sUris.push(uri);
          }
        }
      });
    return [sUris, tUris];
  }, [refUrisDeep]);

  const [neededSimpleUris, cachedSimpleUriResults] = useMemo(() => {
    const needed: string[] = [];
    const cached: Record<string, any> = {};
    nonTableRefUris.forEach(sUri => {
      const res = refDataCache.get(sUri);
      if (res == null) {
        needed.push(sUri);
      } else {
        cached[sUri] = res;
      }
    });
    return [needed, cached];
  }, [nonTableRefUris]);

  const simpleValsResult = useRefsReadBatch({
    refUris: neededSimpleUris,
    skip: neededSimpleUris.length === 0,
  });
  let tableUriProjectId = '';
  let tableUriDigest = '';
  if (tableRefUris.length > 1) {
    throw new Error('Multiple table refs not supported');
  } else if (tableRefUris.length === 1) {
    const tableRef = refUriToObjectVersionKey(tableRefUris[0])!;
    tableUriProjectId = tableRef.entity + '/' + tableRef.project;
    tableUriDigest = tableRef.objectId;
  }
  const tableQueryFilter = useMemo(() => {
    // TODO: tableQuery
    return {};
  }, []);

  const tableRefKey = useMemo(() => {
    return (
      tableRefUris[0] +
      JSON.stringify(tableQueryFilter) +
      params.tableQuery?.limit?.toString()
    );
  }, [params.tableQuery?.limit, tableQueryFilter, tableRefUris]);

  const cachedTableResult = refDataCache.get(tableRefKey);

  const tableQueryParams = useMemo(
    () => ({
      projectId: tableUriProjectId,
      digest: tableUriDigest,
      filter: tableQueryFilter,
      limit: params.tableQuery?.limit,
      skip: tableRefUris.length === 0 || cachedTableResult != null,
    }),
    [
      tableUriProjectId,
      tableUriDigest,
      tableQueryFilter,
      params.tableQuery?.limit,
      tableRefUris,
      cachedTableResult,
    ]
  );
  const tableValsResult = useTableQuery(tableQueryParams);

  return useMemo(() => {
    if (params.refUris.length === 0) {
      return {
        loading: false,
        result: [],
        error: null,
      };
    }
    if (simpleValsResult.loading || tableValsResult.loading) {
      return {
        loading: true,
        result: null,
        error: null,
      };
    }
    const sRes = simpleValsResult.result;
    const tRes = cachedTableResult || tableValsResult.result;

    const valueMap = new Map<string, any>();
    if (sRes != null) {
      sRes.forEach((val, i) => {
        valueMap.set(neededSimpleUris[i], val);
        refDataCache.set(neededSimpleUris[i], val);
      });
    }
    if (tRes != null) {
      valueMap.set(tableRefUris[0], tRes);
      refDataCache.set(tableRefKey, tRes);
    }
    Object.entries(cachedSimpleUriResults).forEach(([uri, val]) => {
      valueMap.set(uri, val);
    });
    const valsResult = params.refUris.map(uri => valueMap.get(uri));

    return {
      loading: false,
      result: valsResult,
      error: null,
    };
  }, [
    params.refUris,
    simpleValsResult.loading,
    simpleValsResult.result,
    tableValsResult.loading,
    tableValsResult.result,
    cachedTableResult,
    cachedSimpleUriResults,
    neededSimpleUris,
    tableRefUris,
    tableRefKey,
  ]);
};

const useTableRowsQuery = (
  params: UseTableRowsQueryParams
): Loadable<traceServerTypes.TraceTableQueryRes> => {
  const getTsClient = useGetTraceServerClientContext();
  const [queryRes, setQueryRes] =
    useState<traceServerTypes.TraceTableQueryRes | null>(null);
  const loadingRef = useRef(false);

  const projectId = projectIdFromParts({
    entity: params.entity,
    project: params.project,
  });

  const doFetch = useCallback(() => {
    if (params.skip) {
      return;
    }
    setQueryRes(null);
    loadingRef.current = true;

    const req: traceServerTypes.TraceTableQueryReq = {
      project_id: projectId,
      digest: params.digest,
      filter: params.filter,
      limit: params.limit,
      offset: params.offset,
      sort_by: params.sortBy,
    };

    getTsClient()
      .tableQuery(req)
      .then(res => {
        loadingRef.current = false;
        setQueryRes(res);
      })
      .catch(err => {
        loadingRef.current = false;
        console.error('Error fetching table rows:', err);
        setQueryRes(null);
      });
  }, [
    getTsClient,
    projectId,
    params.digest,
    params.filter,
    params.limit,
    params.offset,
    params.sortBy,
    params.skip,
  ]);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  return useMemo(() => {
    if (params.skip) {
      return {loading: false, result: null};
    }
    if (queryRes == null || loadingRef.current) {
      return {loading: true, result: null};
    }
    return {loading: false, result: queryRes};
  }, [queryRes, params.skip]);
};

const useTableQueryStats = (
  params: UseTableQueryStatsParams
): Loadable<traceServerTypes.TraceTableQueryStatsBatchRes> => {
  const getTsClient = useGetTraceServerClientContext();
  const [queryRes, setQueryRes] =
    useState<traceServerTypes.TraceTableQueryStatsBatchRes | null>(null);
  const loadingRef = useRef(false);

  const projectId = projectIdFromParts({
    entity: params.entity,
    project: params.project,
  });

  const doFetch = useCallback(() => {
    if (params.skip) {
      return;
    }
    setQueryRes(null);
    loadingRef.current = true;

    const req: traceServerTypes.TraceTableQueryStatsBatchReq = {
      project_id: projectId,
      digests: params.digests,
      include_storage_size: params.includeStorageSize,
    };

    getTsClient()
      .tableQueryStatsBatch(req)
      .then(res => {
        loadingRef.current = false;
        setQueryRes(res);
      })
      .catch(err => {
        loadingRef.current = false;
        console.error('Error fetching table stats:', err);
        setQueryRes(null);
      });
  }, [
    getTsClient,
    projectId,
    params.digests,
    params.includeStorageSize,
    params.skip,
  ]);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  return useMemo(() => {
    if (params.skip) {
      return {loading: false, result: null};
    }
    if (queryRes == null || loadingRef.current) {
      return {loading: true, result: null};
    }
    return {loading: false, result: queryRes};
  }, [queryRes, params.skip]);
};

const useTableUpdate = () => {
  const getTsClient = useGetTraceServerClientContext();

  return useCallback(
    (params: UseTableUpdateParams) => {
      return getTsClient().tableUpdate({
        project_id: params.projectId,
        base_digest: params.baseDigest,
        updates: params.updates,
      });
    },
    [getTsClient]
  );
};

const useApplyMutationsToRef = (): ((
  params: UseApplyMutationsToRefParams
) => Promise<string>) => {
  throw new Error('Not implemented');
};

const useGetRefsType = () => {
  const readBatch = useMakeTraceServerEndpoint(
    'readBatch',
    (params: UseGetRefsTypeParams) => ({
      refs: params.refUris,
    }),
    (res): any[] => res.vals
  );
  return async (params: UseGetRefsTypeParams): Promise<Types.Type[]> => {
    if (params.refUris.length === 0) {
      return [];
    }
    const needed: string[] = [];
    const refToData: Record<string, any> = {};
    params.refUris.forEach(uri => {
      const res = refDataCache.get(uri);
      if (res == null) {
        needed.push(uri);
      } else {
        refToData[uri] = res;
      }
    });
    if (needed.length !== 0) {
      const readBatchResults = await readBatch(params);
      readBatchResults.forEach((res, i) => {
        refToData[needed[i]] = res;
        refDataCache.set(needed[i], res);
      });
    }
    return params.refUris.map(uri => weaveTypeOf(refToData[uri]));
  };
};

export const useCodeForOpRef = (opVersionRef: string): Loadable<string> => {
  const query = useRefsData({refUris: [opVersionRef]});
  const fileSpec = useMemo(() => {
    if (query.result == null) {
      return null;
    }
    const result = query.result[0];
    if (result == null) {
      return null;
    }
    const ref = parseRef(opVersionRef);
    if (isWeaveObjectRef(ref)) {
      const digest = result.files?.['obj.py'];
      if (digest == null) {
        return null;
      }
      return {
        digest,
        entity: ref.entityName,
        project: ref.projectName,
      };
    }
    return null;
  }, [opVersionRef, query.result]);
  const arrayBuffer = useFileContent({
    entity: fileSpec?.entity ?? '',
    project: fileSpec?.project ?? '',
    digest: fileSpec?.digest ?? '',
    skip: fileSpec == null,
  });
  const text = useMemo(() => {
    if (arrayBuffer.loading || query.loading) {
      return {
        loading: true,
        result: null,
      };
    }
    return {
      loading: false,
      result: arrayBuffer.result
        ? new TextDecoder().decode(arrayBuffer.result)
        : null,
    };
  }, [arrayBuffer.loading, arrayBuffer.result, query.loading]);

  return text;
};

const mergeTypes = (a: Types.Type, b: Types.Type): Types.Type => {
  // TODO: this should match the python merge_types implementation.
  if (_.isEqual(a, b)) {
    return a;
  }
  if (isSimpleTypeShape(a) && isSimpleTypeShape(b)) {
    if (a === b) {
      return a;
    } else {
      return union([a, b]);
    }
  }
  if (!isSimpleTypeShape(a) && !isSimpleTypeShape(b)) {
    if (a.type === 'typedDict' && b.type === 'typedDict') {
      const allKeysDict = Object.assign({}, a.propertyTypes, b.propertyTypes);
      const nextPropTypes = _.mapValues(allKeysDict, (value, key) => {
        const selfPropType = a.propertyTypes[key] ?? 'none';
        const otherPropType = b.propertyTypes[key] ?? 'none';
        return mergeTypes(selfPropType, otherPropType);
      });
      return {
        type: 'typedDict',
        propertyTypes: nextPropTypes,
      };
    } else if (a.type === 'list' && b.type === 'list') {
      return {
        type: 'list',
        objectType: mergeTypes(a.objectType, b.objectType),
      };
    } else {
      // This gets very noisy, so commenting out for now.
      // console.warn('unhandled type merge ' + a.type + ' ' + b.type);
    }
  }
  return union([a, b]);
};

const mergeAllTypes = (types: Types.Type[]): Types.Type => {
  return types.reduce(mergeTypes);
};

const weaveTypeOf = (o: any): Types.Type => {
  if (o == null) {
    return 'none';
  } else if (Array.isArray(o)) {
    return {
      type: 'list',
      objectType:
        o.length === 0 ? 'unknown' : mergeAllTypes(o.map(weaveTypeOf)),
    };
  } else if (_.isObject(o)) {
    if ('_type' in o) {
      return {
        type: (o as any)._type,
        _base_type: {type: 'Object'},
        _is_object: true,
        ..._.mapValues(_.omit(o, ['_type']), weaveTypeOf),
      } as any;
    } else {
      return {
        type: 'typedDict',
        propertyTypes: _.mapValues(o, weaveTypeOf),
      } as any;
    }
  } else if (_.isString(o)) {
    if (o.startsWith(WANDB_ARTIFACT_REF_PREFIX)) {
      return {
        type: 'WandbArtifactRef',
      };
    } else if (o.startsWith(WEAVE_REF_PREFIX)) {
      return {type: 'Ref'};
    }
    return 'string';
  } else if (_.isNumber(o)) {
    return 'number'; // TODO
  } else if (_.isBoolean(o)) {
    return 'boolean';
  }
  throw new Error('Type conversion not implemented for value: ' + o);
};

const useRefsType = (params: UseGetRefsTypeParams): Loadable<Types.Type[]> => {
  const dataResult = useRefsData({refUris: params.refUris});
  const finalRes = useMemo(() => {
    if (!dataResult.loading) {
      return {
        loading: false,
        result: dataResult.result?.map(weaveTypeOf) ?? [],
        error: null,
      };
    } else {
      return {
        loading: true,
        result: null,
        error: null,
      };
    }
  }, [dataResult.loading, dataResult.result]);
  return finalRes;
};

/// Converters ///

const statusCode = (
  serverSideStatus: traceServerTypes.ComputedCallStatusType | undefined,
  exception: string | null | undefined,
  endedAt: string | null | undefined,
  statusCounts: {error: number} | undefined,
  hasDescendantErrors?: boolean
) => {
  if (serverSideStatus) {
    if (
      serverSideStatus === traceServerTypes.ComputedCallStatuses.success &&
      hasDescendantErrors
    ) {
      return traceServerTypes.ComputedCallStatuses.descendant_error;
    }
    return serverSideStatus;
  }
  if (exception) {
    return traceServerTypes.ComputedCallStatuses.error;
  } else if (endedAt) {
    const errors = statusCounts?.error ?? 0;
    if (errors > 0 || hasDescendantErrors) {
      return traceServerTypes.ComputedCallStatuses.descendant_error;
    }
    return traceServerTypes.ComputedCallStatuses.success;
  }
  return traceServerTypes.ComputedCallStatuses.running;
};

/**
 * Getting the status code for a trace call has a few complexities.
 */
export const traceCallStatusCode = (
  traceCall: traceServerTypes.TraceCallSchema,
  hasDescendantErrors?: boolean
): traceServerTypes.ComputedCallStatusType => {
  return statusCode(
    traceCall.summary?.weave?.status,
    traceCall.exception,
    traceCall.ended_at,
    traceCall.summary?.status_counts,
    hasDescendantErrors
  );
};

/**
 * Getting the status code for a trace call has a few complexities.
 */
export const flattenedTraceCallStatusCode = (
  traceCall: {[key: string]: any},
  hasDescendantErrors?: boolean
): traceServerTypes.ComputedCallStatusType => {
  return statusCode(
    traceCall['summary.weave.status'],
    traceCall.exception,
    traceCall.ended_at,
    traceCall['summary.status_counts.error'],
    hasDescendantErrors
  );
};

export const traceCallLatencyS = (
  traceCall: traceServerTypes.TraceCallSchema
) => {
  return traceCallLatencyMs(traceCall) / 1000;
};

export const traceCallLatencyMs = (
  traceCall: traceServerTypes.TraceCallSchema
) => {
  const startDate = convertISOToDate(traceCall.started_at);
  const endDate = traceCall.ended_at
    ? convertISOToDate(traceCall.ended_at)
    : null;
  if (startDate == null || endDate == null) {
    return 0;
  }
  return endDate.getTime() - startDate.getTime();
};

const traceCallToLegacySpan = (
  traceCall: traceServerTypes.TraceCallSchema
): RawSpanFromStreamTableEra => {
  const startDate = convertISOToDate(traceCall.started_at);
  const endDate = traceCall.ended_at
    ? convertISOToDate(traceCall.ended_at)
    : null;
  const summary = {
    latency_s: traceCallLatencyS(traceCall),
    ...traceCall.summary,
  };

  // This is a very specific hack to make sure that the output is always an
  // object. After the clickhouse migration, we no longer have this constraint.
  // Before, if the output was a simple type, it would be wrapped in an object
  // with the key '_result'. The rest of the codebase expects this, so we're
  // keeping it for now. However, this is causing some weirdness in the UI so we
  // should remove it soon. When we do that, we can also remove this hack.
  const unknownOutput = traceCall.output;
  let output: {[key: string]: any};
  if (
    typeof unknownOutput === 'object' &&
    unknownOutput !== null &&
    !Array.isArray(unknownOutput)
  ) {
    // If the output is already an object, we don't need to do anything.
    output = unknownOutput as {[key: string]: any};
  } else {
    // If the output is a simple type, we wrap it in an object with the key
    // '_result'.
    output = {_result: unknownOutput as any};
  }
  return {
    name: traceCall.op_name,
    inputs: traceCall.inputs,
    output,
    status_code: traceCallStatusCode(traceCall),
    exception: traceCall.exception ?? undefined,
    attributes: traceCall.attributes,
    summary,
    span_id: traceCall.id,
    trace_id: traceCall.trace_id,
    parent_id: traceCall.parent_id ?? undefined,
    timestamp: startDate.getTime(),
    start_time_ms: startDate.getTime(),
    end_time_ms: endDate?.getTime(),
  };
};

const projectIdToParts = (projectId: string) => {
  const [entity, project] = projectId.split('/');
  return {entity, project};
};

// Hack - underlying client should be updated to handle deleted projects better.
const isValidTraceCall = (callRes: traceServerTypes.TraceCallSchema) => {
  return !('detail' in callRes);
};

export const privateRefToSimpleName = (ref: string) => {
  const trimmed = ref.replace(`${WEAVE_PRIVATE_PREFIX}//`, '');
  try {
    return trimmed.split('/')[1].split(':')[0];
  } catch (e) {
    return trimmed;
  }
};

export const parseSpanName = (opName: string) => {
  if (
    opName.startsWith(WANDB_ARTIFACT_REF_PREFIX) ||
    opName.startsWith(WEAVE_REF_PREFIX)
  ) {
    return opVersionRefOpName(opName);
  }
  if (opName.startsWith(WEAVE_PRIVATE_PREFIX)) {
    return privateRefToSimpleName(opName);
  }
  return opName;
};

export const traceCallToUICallSchema = (
  traceCall: traceServerTypes.TraceCallSchema
): CallSchema => {
  const {entity, project} = projectIdToParts(traceCall.project_id);

  return {
    entity,
    project,
    callId: traceCall.id,
    traceId: traceCall.trace_id,
    parentId: traceCall.parent_id ?? null,
    spanName: parseSpanName(traceCall.op_name),
    displayName: traceCall.display_name ?? null,
    opVersionRef:
      traceCall.op_name.startsWith(WANDB_ARTIFACT_REF_PREFIX) ||
      traceCall.op_name.startsWith(WEAVE_REF_PREFIX) ||
      traceCall.op_name.startsWith(WEAVE_PRIVATE_PREFIX)
        ? traceCall.op_name
        : null,
    rawSpan: traceCallToLegacySpan(traceCall),
    rawFeedback: {},
    userId: traceCall.wb_user_id ?? null,
    runId: traceCall.wb_run_id ?? null,
    traceCall,
  };
};

export const useObjCreate = () => {
  const getTsClient = useGetTraceServerClientContext();

  return useCallback(
    (params: UseObjCreateParams) => {
      return getTsClient()
        .objCreate({
          obj: {
            project_id: params.projectId,
            object_id: params.objectId,
            val: params.val,
            builtin_object_class: params.baseObjectClass,
          },
        })
        .then(res => {
          return res.digest;
        });
    },
    [getTsClient]
  );
};

/**
 * Returns a callback that defers object-change listener notifications until
 * the wrapped async function completes, so multiple object writes appear as
 * a single atomic update to the UI (avoiding intermediate refetches).
 */
export const useBatchObjectChanges = () => {
  const getTsClient = useGetTraceServerClientContext();
  return useCallback(
    async <T>(fn: () => Promise<T>): Promise<T> => {
      const client = getTsClient();
      client.beginBatchObjectChanges();
      try {
        return await fn();
      } finally {
        client.endBatchObjectChanges();
      }
    },
    [getTsClient]
  );
};

export const useScorerCreate = () => {
  const objCreate = useObjCreate();

  return useCallback(
    async ({
      entity,
      project,
      name,
      val,
      baseObjectClass,
    }: {
      entity: string;
      project: string;
      name: string;
      val: any;
      baseObjectClass?: string;
    }): Promise<WeaveObjectVersionKey> => {
      const scorerObj = {
        _type: name,
        name: name,
        description: `${name} created from the UI.`,
        ref: null,
        column_map: null,
        _class_name: name,
        _bases: ['Scorer', 'Object', 'BaseModel'],
        ...val,
      };

      const scorerDigest = await objCreate({
        projectId: `${entity}/${project}`,
        objectId: name,
        val: scorerObj,
        baseObjectClass,
      });

      const objectVersionKey: WeaveObjectVersionKey = {
        scheme: 'weave',
        entity,
        project,
        weaveKind: 'object',
        objectId: name,
        versionHash: scorerDigest,
        path: '',
      };

      return objectVersionKey;
    },
    [objCreate]
  );
};

export const useTableCreate = (): ((
  table: traceServerTypes.TableCreateReq
) => Promise<traceServerTypes.TableCreateRes>) => {
  const getTsClient = useGetTraceServerClientContext();

  return useCallback(
    (table: traceServerTypes.TableCreateReq) => {
      return getTsClient().tableCreate(table);
    },
    [getTsClient]
  );
};

export const useProjectIngestSamplingSettings = (projectId: string) => {
  const getTsClient = useGetTraceServerClientContext();

  return useAsync(async () => {
    return getTsClient().projectIngestSamplingSettingsRead({
      project_id: projectId,
    });
  }, [getTsClient, projectId]);
};

export const useProjectStats = (projectId: string) => {
  const getTsClient = useGetTraceServerClientContext();

  return useAsync(async () => {
    return getTsClient().projectStats({project_id: projectId});
  }, [getTsClient, projectId]);
};

/**
 * Hook wrapping the /feedback/aggregate request.
 */
export const useFeedbackAggregate = (
  req: traceServerTypes.FeedbackAggregateReq,
  opts?: {skip?: boolean}
) => {
  const getTsClient = useGetTraceServerClientContext();
  const skip = opts?.skip ?? false;
  return useAsyncRetry(async () => {
    if (skip) {
      return null;
    }
    return getTsClient().feedbackAggregate(req);
    // req is serialized below to avoid refiring the request every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTsClient, skip, JSON.stringify(req)]);
};

export const useProjectTTLSettings = (projectId: string) => {
  const getTsClient = useGetTraceServerClientContext();

  return useAsyncRetry(async () => {
    return getTsClient().projectTTLSettingsRead({project_id: projectId});
  }, [getTsClient, projectId]);
};

export const useUpdateProjectTTLSettings = () => {
  const getTsClient = useGetTraceServerClientContext();

  return useCallback(
    (projectId: string, retentionDays: number | null) => {
      return getTsClient().projectTTLSettingsUpdate({
        project_id: projectId,
        retention_days: retentionDays,
      });
    },
    [getTsClient]
  );
};

// Helper function to process first turn inputs
const processFirstTurnInputs = async (
  getTsClient: () => traceServerClient.TraceServerClient,
  projectId: string,
  groupedCalls: {[turnId: string]: traceServerTypes.TraceCallSchema[]}
): Promise<Map<string, traceServerTypes.TraceCallSchema['inputs']>> => {
  const firstTurnsInputs = new Map();
  const firstCallsPerTurn = Object.entries(groupedCalls)
    .map(([turnId, calls]) => {
      // Build set of all parent IDs within this turn
      const parentIds = new Set(
        calls.map(call => call.parent_id).filter(Boolean)
      );

      // Find leaf calls (calls whose ID is not a parent of any other call)
      const leafCalls = calls.filter(call => !parentIds.has(call.id));

      // Filter leaf calls for LLM calls (those with usage data)
      const llmLeafCalls = leafCalls.filter(
        call => !isEmpty(call.summary?.usage)
      );

      if (llmLeafCalls.length === 0) {
        return null;
      }

      // Sort by started_at ascending to get the first LLM leaf call
      const sortedLlmLeafCalls = llmLeafCalls.sort((a, b) => {
        return (
          new Date(a.started_at).getTime() - new Date(b.started_at).getTime()
        );
      });

      return {turnId, call: sortedLlmLeafCalls[0]};
    })
    .filter(Boolean);

  // Get inputs for first calls
  if (firstCallsPerTurn.length > 0) {
    const callInputs = await getTsClient().callsStreamQuery({
      project_id: projectId,
      filter: {call_ids: firstCallsPerTurn.map(item => item!.call.id)},
      columns: ['inputs', 'turn_id'],
    });

    (callInputs.calls ?? []).forEach(call => {
      firstTurnsInputs.set(call.turn_id, call.inputs);
    });
  }

  return firstTurnsInputs;
};

// Helper function to process last turn outputs and statuses
const processLastTurnData = async (
  getTsClient: () => traceServerClient.TraceServerClient,
  projectId: string,
  groupedCalls: {[turnId: string]: traceServerTypes.TraceCallSchema[]}
): Promise<{
  outputs: Map<string, traceServerTypes.TraceCallSchema['output']>;
  statuses: Map<string, string>;
}> => {
  const lastTurnsOutputs = new Map();
  const lastTurnsStatuses = new Map();

  const lastCallsPerTurn = Object.entries(groupedCalls)
    .map(([turnId, calls]) => {
      // Build set of all parent IDs within this turn
      const parentIds = new Set(
        calls.map(call => call.parent_id).filter(Boolean)
      );

      // Find leaf calls (calls whose ID is not a parent of any other call)
      const leafCalls = calls.filter(call => !parentIds.has(call.id));

      // Filter leaf calls for LLM calls (those with usage data)
      const llmLeafCalls = leafCalls.filter(
        call => !isEmpty(call.summary?.usage)
      );

      if (llmLeafCalls.length === 0) {
        return null;
      }

      // Sort by ended_at descending to get the last completed LLM leaf call
      const sortedLlmLeafCalls = llmLeafCalls.sort((a, b) => {
        // Handle cases where ended_at might be null (still running calls)
        const aEndTime = a.ended_at ? new Date(a.ended_at).getTime() : 0;
        const bEndTime = b.ended_at ? new Date(b.ended_at).getTime() : 0;
        return bEndTime - aEndTime;
      });

      return {turnId, call: sortedLlmLeafCalls[0]};
    })
    .filter(Boolean);

  // Process last turn statuses (using the same data from the main query)
  Object.entries(groupedCalls).forEach(([turnId, calls]) => {
    // Since turn IDs are call IDs, find the turn call itself
    const turnCall = calls.find(call => call.id === turnId);
    if (turnCall) {
      // Use same logic as flattenedTraceCallStatusCode
      let status;
      if (turnCall.exception) {
        status = ComputedCallStatuses.error;
      } else if (turnCall.ended_at) {
        status = ComputedCallStatuses.success;
      } else {
        status = ComputedCallStatuses.running;
      }
      lastTurnsStatuses.set(turnId, status);
    }
  });

  // Get outputs for last calls
  if (lastCallsPerTurn.length > 0) {
    const callOutputs = await getTsClient().callsStreamQuery({
      project_id: projectId,
      filter: {call_ids: lastCallsPerTurn.map(item => item!.call.id)},
      columns: ['output', 'turn_id'],
    });

    (callOutputs.calls ?? []).forEach(call => {
      lastTurnsOutputs.set(call.turn_id, call.output);
    });
  }

  return {outputs: lastTurnsOutputs, statuses: lastTurnsStatuses};
};

const useTurnCallsData = (
  projectId: string,
  firstTurnIds: string[],
  lastTurnIds: string[]
): {
  dataLoading: boolean;
  dataError: Error | undefined;
  firstTurnsLoading: boolean;
  firstTurnsError: Error | undefined;
  firstTurnsInputs: Map<string, traceServerTypes.TraceCallSchema['inputs']>;
  lastTurnsLoading: boolean;
  lastTurnsError: Error | undefined;
  lastTurnsOutputs: Map<string, traceServerTypes.TraceCallSchema['output']>;
  lastTurnsStatuses: Map<string, ComputedCallStatusType>;
} => {
  const getTsClient = useGetTraceServerClientContext();

  // Union of all turn IDs to make a single request
  const allTurnIds = useMemo(() => {
    const union = new Set([...firstTurnIds, ...lastTurnIds]);
    return Array.from(union);
  }, [firstTurnIds, lastTurnIds]);

  // First step: Load all calls and group them
  const [groupedCallsResult, loadGroupedCalls] = useAsyncFn(async () => {
    if (!allTurnIds || allTurnIds.length === 0) {
      return null;
    }

    // Single request to get all calls for all turn IDs
    const result = await getTsClient().callsStreamQuery({
      project_id: projectId,
      filter: {turn_ids: allTurnIds},
      columns: [
        'id',
        'parent_id',
        'summary',
        'turn_id',
        'started_at',
        'ended_at',
        'exception',
      ],
    });

    // Group calls by turn_id
    const groupedCalls = _.groupBy(result.calls, 'turn_id');

    const firstTurnIdsSet = new Set(firstTurnIds);
    const lastTurnIdsSet = new Set(lastTurnIds);

    // Separate the groups for first and last turn processing using Sets for O(1) lookup
    const firstTurnGroups = Object.fromEntries(
      Object.entries(groupedCalls).filter(([turnId]) =>
        firstTurnIdsSet.has(turnId)
      )
    );
    const lastTurnGroups = Object.fromEntries(
      Object.entries(groupedCalls).filter(([turnId]) =>
        lastTurnIdsSet.has(turnId)
      )
    );

    return {
      firstTurnGroups,
      lastTurnGroups,
    };
  }, [getTsClient, projectId, allTurnIds]);

  // Second step: Process first turn inputs (runs when groupedCallsResult is ready)
  const [firstTurnsInputsResult, loadFirstTurnsInputs] =
    useAsyncFn(async () => {
      if (!groupedCallsResult.value?.firstTurnGroups) {
        return new Map();
      }
      return await processFirstTurnInputs(
        getTsClient,
        projectId,
        groupedCallsResult.value.firstTurnGroups
      );
    }, [getTsClient, projectId, groupedCallsResult.value]);

  // Third step: Process last turn data (runs when groupedCallsResult is ready)
  const [lastTurnsDataResult, loadLastTurnsData] = useAsyncFn(async () => {
    if (!groupedCallsResult.value?.lastTurnGroups) {
      return {outputs: new Map(), statuses: new Map()};
    }
    const r = await processLastTurnData(
      getTsClient,
      projectId,
      groupedCallsResult.value.lastTurnGroups
    );
    return r;
  }, [getTsClient, projectId, groupedCallsResult.value]);

  // Trigger the initial data loading
  useEffect(() => {
    if (allTurnIds && allTurnIds.length > 0) {
      loadGroupedCalls();
    }
  }, [allTurnIds, loadGroupedCalls]);

  // Trigger the processing when grouped data is ready
  useEffect(() => {
    if (groupedCallsResult.value) {
      loadFirstTurnsInputs();
      loadLastTurnsData();
    }
  }, [groupedCallsResult.value, loadFirstTurnsInputs, loadLastTurnsData]);

  // Return separate states for each processing thread
  return useMemo(() => {
    return {
      // Data loading state
      dataLoading: groupedCallsResult.loading,
      dataError: groupedCallsResult.error,

      // First turn processing state
      firstTurnsLoading:
        groupedCallsResult.loading || firstTurnsInputsResult.loading,
      firstTurnsError: firstTurnsInputsResult.error,
      firstTurnsInputs: firstTurnsInputsResult.value ?? new Map(),

      // Last turn processing state
      lastTurnsLoading:
        groupedCallsResult.loading || lastTurnsDataResult.loading,
      lastTurnsError: lastTurnsDataResult.error,
      lastTurnsOutputs: lastTurnsDataResult.value?.outputs ?? new Map(),
      lastTurnsStatuses: lastTurnsDataResult.value?.statuses ?? new Map(),
    };
  }, [
    groupedCallsResult.loading,
    groupedCallsResult.error,
    firstTurnsInputsResult.loading,
    firstTurnsInputsResult.error,
    firstTurnsInputsResult.value,
    lastTurnsDataResult.loading,
    lastTurnsDataResult.error,
    lastTurnsDataResult.value,
  ]);
};

export const useThreadsQuery = (
  params: traceServerTypes.ThreadsQueryReq
): {
  threadsState: ReturnType<
    typeof useAsyncRetry<traceServerTypes.ThreadsQueryRes>
  >;
  turnCallsDataResult: ReturnType<typeof useTurnCallsData>;
} => {
  const getTsClient = useGetTraceServerClientContext();
  const deepParams = useDeepMemo(params);

  const [{firstTurnIds, lastTurnIds}, setTurnIds] = useState<{
    firstTurnIds: string[];
    lastTurnIds: string[];
  }>({firstTurnIds: [], lastTurnIds: []});

  const threadsState = useAsyncRetry(async () => {
    const result = await getTsClient().threadsStreamQuery(deepParams);
    const threads = result.threads ?? [];

    setTurnIds({
      firstTurnIds: threads
        .map(thread => thread.first_turn_id!)
        .filter(Boolean),
      lastTurnIds: threads.map(thread => thread.last_turn_id!).filter(Boolean),
    });

    return result;
  }, [getTsClient, deepParams]);

  const turnCallsDataResult = useTurnCallsData(
    deepParams.project_id,
    firstTurnIds,
    lastTurnIds
  );

  return {
    threadsState,
    turnCallsDataResult,
  };
};

export const useThreadTurns = (
  projectId: string,
  threadId: string,
  pageSize: number
): {
  turnsState: AsyncState<{
    turnCalls: traceServerTypes.TraceCallSchema[];
    thread: traceServerTypes.ThreadSchema;
  }>;
  totalTurns: number;
  currentPage: number;
  totalPages: number;
  turnPrevPage: () => void;
  turnNextPage: () => void;
  turnsLoading: boolean;
  turnsError: Error | undefined;
} => {
  const getTsClient = useGetTraceServerClientContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTurns, setTotalTurns] = useState(0);

  // Step 1: Thread metadata hook - only depends on threadId
  const threadMetadata = useAsync(async () => {
    const thread = await getTsClient().threadsStreamQuery({
      project_id: projectId,
      filter: {thread_ids: [threadId]},
    });

    if (thread.threads.length === 0) {
      throw new Error(`Thread ${threadId} not found`);
    }

    const threadData = thread.threads[0];
    const totalTurnsCount = threadData.turn_count;
    setTotalTurns(totalTurnsCount);

    // Default page logic - jump to last page (most recent turns)
    const totalPages = Math.ceil(totalTurnsCount / pageSize);
    if (currentPage === 1 && totalPages > 0) {
      setCurrentPage(totalPages);
    }

    return threadData;
  }, [getTsClient, threadId, pageSize]);

  // Step 2: Turn calls hook - depends on metadata + pagination
  const turnCalls = useAsync(async () => {
    if (!threadMetadata.value) return null; // Wait for metadata

    // Calculate offset for pagination (0-based for API)
    const offset = (currentPage - 1) * pageSize;

    const result = await getTsClient().callsStreamQuery({
      project_id: projectId,
      query: {
        $expr: {
          $and: [
            {$eq: [{$getField: 'thread_id'}, {$literal: threadId}]},
            {$eq: [{$getField: 'id'}, {$getField: 'turn_id'}]},
          ],
        },
      },
      sort_by: [{field: 'ended_at', direction: 'asc'}],
      limit: pageSize,
      offset: offset,
      columns: [
        'id',
        'turn_id',
        'started_at',
        'ended_at',
        'exception',
        'inputs',
        'op_name',
      ],
    });

    return result.calls ?? [];
  }, [getTsClient, threadId, pageSize, currentPage, threadMetadata.value]);

  // Step 3: Combined turnsState
  const turnsState = useMemo((): AsyncState<{
    turnCalls: traceServerTypes.TraceCallSchema[];
    thread: traceServerTypes.ThreadSchema;
  }> => {
    if (threadMetadata.loading) {
      return {loading: true};
    }
    if (threadMetadata.error) {
      return {loading: false, error: threadMetadata.error};
    }
    if (threadMetadata.value && turnCalls.value) {
      return {
        loading: false,
        value: {
          thread: threadMetadata.value,
          turnCalls: turnCalls.value,
        },
      };
    }
    return {loading: true};
  }, [threadMetadata, turnCalls]);

  const totalPages = Math.ceil(totalTurns / pageSize);

  const turnPrevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  }, [currentPage]);

  const turnNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  }, [currentPage, totalPages]);

  return {
    turnsState,
    totalTurns,
    currentPage,
    totalPages,
    turnPrevPage,
    turnNextPage,
    turnsLoading: turnCalls.loading, // Separate loading state for turns
    turnsError: turnCalls.error, // Separate error state for turns
  };
};

export const useThreadMessageIds = (
  projectId: string,
  turnIds: string[]
): AsyncState<Array<string>> => {
  const getTsClient = useGetTraceServerClientContext();
  return useAsync(async () => {
    const result = await getTsClient().callsStreamQuery({
      project_id: projectId,
      filter: {
        turn_ids: turnIds,
      },
      columns: ['id', 'parent_id', 'summary', 'started_at'],
    });

    const calls = result.calls ?? [];

    const parentIds = new Set(
      calls.map(call => call.parent_id).filter(Boolean)
    );

    // Find leaf calls (calls whose ID is not a parent of any other call)
    const leafCalls = calls.filter(call => !parentIds.has(call.id));

    // Filter leaf calls for LLM calls (those with usage data)
    const llmLeafCalls = leafCalls.filter(
      call => !isEmpty(call.summary?.usage)
    );

    if (llmLeafCalls.length === 0) {
      return [];
    }

    return _.map(llmLeafCalls, 'id');
  }, [getTsClient, turnIds]);
};

/**
 * Loads message data for a thread, handling large numbers of message IDs by batching requests.
 *
 * @param projectId - The project ID containing the thread
 * @param turnIds - A group of turn IDs to load messages for
 * @returns AsyncState containing the loaded message calls
 */
export const useThreadMessagesLoader = (
  projectId: string,
  turnIds: string[]
) => {
  const getTsClient = useGetTraceServerClientContext();

  const messageIdsState = useThreadMessageIds(projectId, turnIds);
  const [loadedAllData, setLoadedAllData] = useState(false);

  const [loadMessagesState, loadMessages] = useAsyncFn(async () => {
    if (messageIdsState.loading) {
      return [];
    }

    const messageIds = messageIdsState.value;
    if (!messageIds || messageIds.length === 0) {
      setLoadedAllData(true); // Mark as loaded even if no messages
      return [];
    }

    // If we have more than 500 call_ids, we need to make multiple requests
    const BATCH_SIZE = 500;
    const batches: string[][] = [];

    for (let i = 0; i < messageIds.length; i += BATCH_SIZE) {
      batches.push(messageIds.slice(i, i + BATCH_SIZE));
    }

    // Make all requests in parallel
    const results = await Promise.all(
      batches.map(batch =>
        getTsClient().callsStreamQuery({
          project_id: projectId,
          filter: {call_ids: batch},
          sort_by: [{field: 'started_at', direction: 'asc'}],
          limit: 1000,
          columns: [
            'id',
            'started_at',
            'turn_id',
            'inputs',
            'output',
            'attributes',
            'otel_dump',
          ],
          expand_columns: ['inputs', 'output'], // Server-side ref expansion
        })
      )
    );

    // Combine all results
    const allCalls: traceServerTypes.TraceCallSchema[] = [];
    results.forEach(result => {
      allCalls.push(...(result.calls ?? []));
    });

    // Sort the final results by started_at to maintain order across batches
    allCalls.sort((a, b) => {
      return (
        new Date(a.started_at).getTime() - new Date(b.started_at).getTime()
      );
    });
    setLoadedAllData(true);

    return allCalls;
  }, [getTsClient, projectId, turnIds, messageIdsState.value]);

  // Reset loaded state when turnIds change
  useEffect(() => {
    setLoadedAllData(false);
  }, [turnIds]);

  useEffect(() => {
    if (messageIdsState.value) {
      loadMessages();
    }
  }, [messageIdsState.value, loadMessages]);

  return {messagesState: loadMessagesState, loaded: loadedAllData};
};

export type UsageTimeRangeMode = 'week' | 'day' | 'hour' | 'minute';

export interface UseUsageAnalyticsParams {
  entity: string;
  project: string;
  mode: UsageTimeRangeMode;
  pageOffset: number;
  skip?: boolean;
}

export const TIME_RANGE_CONFIG: Record<
  UsageTimeRangeMode,
  {durationMs: number; granularitySeconds: number}
> = {
  week: {durationMs: 7 * 24 * 60 * 60 * 1000, granularitySeconds: 12 * 60 * 60},
  day: {durationMs: 24 * 60 * 60 * 1000, granularitySeconds: 60 * 60},
  hour: {durationMs: 60 * 60 * 1000, granularitySeconds: 2 * 60},
  minute: {durationMs: 60 * 1000, granularitySeconds: 2},
};

export const useCallStats = (
  params: UseUsageAnalyticsParams
): LoadableWithError<traceServerTypes.CallStatsRes | null> => {
  const getTsClient = useGetTraceServerClientContext();
  const [result, setResult] = useState<
    LoadableWithError<traceServerTypes.CallStatsRes | null>
  >({
    loading: true,
    result: null,
    error: null,
  });

  const deepParams = useDeepMemo(params);

  useEffect(() => {
    if (deepParams.skip) {
      setResult({loading: false, result: null, error: null});
      return;
    }

    setResult({loading: true, result: null, error: null});

    const config = TIME_RANGE_CONFIG[deepParams.mode];
    const now = Date.now();
    const offsetMs = deepParams.pageOffset * config.durationMs;
    const end = new Date(now - offsetMs);
    const start = new Date(end.getTime() - config.durationMs);

    const req: traceServerTypes.CallStatsReq = {
      project_id: `${deepParams.entity}/${deepParams.project}`,
      start: start.toISOString(),
      end: end.toISOString(),
      granularity: config.granularitySeconds,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      filter: {
        trace_roots_only: true,
      },
      usage_metrics: [
        {metric: 'input_tokens', aggregations: ['sum']},
        {metric: 'output_tokens', aggregations: ['sum']},
        {metric: 'input_cost', aggregations: ['sum']},
        {metric: 'output_cost', aggregations: ['sum']},
      ],
    };

    getTsClient()
      .callStats(req)
      .then(res => {
        setResult({loading: false, result: res, error: null});
      })
      .catch(err => {
        setResult({loading: false, result: null, error: err});
      });
  }, [getTsClient, deepParams]);

  return result;
};

export const useCallMetricsStats = (
  params: UseUsageAnalyticsParams
): LoadableWithError<traceServerTypes.CallStatsRes | null> => {
  const getTsClient = useGetTraceServerClientContext();
  const [result, setResult] = useState<
    LoadableWithError<traceServerTypes.CallStatsRes | null>
  >({
    loading: true,
    result: null,
    error: null,
  });

  const deepParams = useDeepMemo(params);

  useEffect(() => {
    if (deepParams.skip) {
      setResult({loading: false, result: null, error: null});
      return;
    }

    setResult({loading: true, result: null, error: null});

    const config = TIME_RANGE_CONFIG[deepParams.mode];
    const now = Date.now();
    const offsetMs = deepParams.pageOffset * config.durationMs;
    const end = new Date(now - offsetMs);
    const start = new Date(end.getTime() - config.durationMs);

    const req: traceServerTypes.CallStatsReq = {
      project_id: `${deepParams.entity}/${deepParams.project}`,
      start: start.toISOString(),
      end: end.toISOString(),
      granularity: config.granularitySeconds,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      filter: {
        trace_roots_only: true,
      },
      call_metrics: [
        {metric: 'call_count', aggregations: ['sum']},
        {metric: 'error_count', aggregations: ['sum']},
        {
          metric: 'latency_ms',
          aggregations: ['avg', 'max'],
          percentiles: [50, 95, 99],
        },
      ],
    };

    getTsClient()
      .callStats(req)
      .then(res => {
        setResult({loading: false, result: res, error: null});
      })
      .catch(err => {
        setResult({loading: false, result: null, error: err});
      });
  }, [getTsClient, deepParams]);

  return result;
};

/// Tag & Alias Hooks ///

export const useAddTags = () => {
  const getTsClient = useGetTraceServerClientContext();
  return useCallback(
    (projectId: string, objectId: string, digest: string, tags: string[]) => {
      return getTsClient().objAddTags({
        project_id: projectId,
        object_id: objectId,
        digest,
        tags,
      });
    },
    [getTsClient]
  );
};

export const useRemoveTags = () => {
  const getTsClient = useGetTraceServerClientContext();
  return useCallback(
    (projectId: string, objectId: string, digest: string, tags: string[]) => {
      return getTsClient().objRemoveTags({
        project_id: projectId,
        object_id: objectId,
        digest,
        tags,
      });
    },
    [getTsClient]
  );
};

export const useSetAliases = () => {
  const getTsClient = useGetTraceServerClientContext();
  return useCallback(
    (
      projectId: string,
      objectId: string,
      digest: string,
      aliases: string[]
    ) => {
      return getTsClient().objSetAliases({
        project_id: projectId,
        object_id: objectId,
        digest,
        aliases,
      });
    },
    [getTsClient]
  );
};

export const useRemoveAliases = () => {
  const getTsClient = useGetTraceServerClientContext();
  return useCallback(
    (projectId: string, objectId: string, aliases: string[]) => {
      return getTsClient().objRemoveAliases({
        project_id: projectId,
        object_id: objectId,
        aliases,
      });
    },
    [getTsClient]
  );
};

function schemaPathsToMetrics(
  paths: traceServerTypes.FeedbackPayloadPath[]
): traceServerTypes.FeedbackMetricSpec[] {
  return paths
    .filter(p => (p.value_type ?? 'numeric') !== 'categorical')
    .map(p => {
      const valueType = p.value_type ?? 'numeric';
      if (valueType === 'boolean') {
        return {
          json_path: p.json_path,
          value_type: valueType,
          aggregations: ['count_true', 'count_false'],
        };
      }
      return {
        json_path: p.json_path,
        value_type: valueType,
        aggregations: ['min', 'avg', 'max'],
        percentiles: [1, 5, 50, 95, 99],
      };
    });
}

export interface UseFeedbackStatsParams {
  entity: string;
  project: string;
  triggerRef: string;
  mode: UsageTimeRangeMode;
  pageOffset?: number;
  granularitySecondsOverride?: number;
  skip?: boolean;
}

export const useFeedbackStats = (
  params: UseFeedbackStatsParams
): LoadableWithError<traceServerTypes.FeedbackStatsRes | null> => {
  const getTsClient = useGetTraceServerClientContext();
  const [result, setResult] = useState<
    LoadableWithError<traceServerTypes.FeedbackStatsRes | null>
  >({
    loading: true,
    result: null,
    error: null,
  });

  const deepParams = useDeepMemo(params);

  useEffect(() => {
    if (deepParams.skip) {
      setResult({loading: false, result: null, error: null});
      return;
    }

    setResult({loading: true, result: null, error: null});

    const config = TIME_RANGE_CONFIG[deepParams.mode];
    const now = Date.now();
    const offsetMs = (deepParams.pageOffset ?? 0) * config.durationMs;
    const end = new Date(now - offsetMs);
    const start = new Date(end.getTime() - config.durationMs);
    const projectId = `${deepParams.entity}/${deepParams.project}`;

    const client = getTsClient();

    client
      .feedbackPayloadSchema({
        project_id: projectId,
        start: start.toISOString(),
        end: end.toISOString(),
        trigger_ref: deepParams.triggerRef,
      })
      .then(schemaRes => {
        const metrics = schemaPathsToMetrics(schemaRes.paths ?? []);
        return client.feedbackStats({
          project_id: projectId,
          start: start.toISOString(),
          end: end.toISOString(),
          granularity:
            deepParams.granularitySecondsOverride ?? config.granularitySeconds,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          trigger_ref: deepParams.triggerRef,
          metrics,
        });
      })
      .then(res => {
        setResult({loading: false, result: res, error: null});
      })
      .catch(err => {
        setResult({loading: false, result: null, error: err});
      });
  }, [getTsClient, deepParams]);

  return result;
};

/// Utility Functions ///

export const convertISOToDate = (iso: string): Date => {
  return new Date(iso);
};

export const tsWFDataModelHooks: WFDataModelHooksInterface = {
  useCall,
  useCalls,
  useCallsStats,
  useProjectHasCalls,
  useCallsDeleteFunc,
  useCallUpdateFunc,
  useCallsExport,
  useObjCreate,
  useOpVersion,
  useOpVersions,
  useObjectVersion,
  useObjectDeleteFunc,
  useRootObjectVersions,
  useRefsData,
  useApplyMutationsToRef,
  useFeedback,
  useFileContent,
  useTableRowsQuery,
  useTableQueryStats,
  useTableUpdate,
  useTableCreate,
  derived: {
    useGetRefsType,
    useRefsType,
    useCodeForOpRef,
  },
};
